<template>
    <div id="campaign-form" class="salesforce-campaign-form">
        <DxValidationGroup>
            <!-- Toolbar -->
            <div class="form-toolbar">
                <ToolbarForm :is-editing="isEditing" :is-disabled="isLocked" @edit-canceled="cancelEdit()"
                    @edit-toggled="startEdit()" @save-clicked="handleSaveClick" />
            </div>
            <div class="info-card">

                <div class="card-grid">
                    <div class="field-group full-width">
                        <label class="field-label">Denumire Campanie</label>
                        <FormTextbox v-model="internalCampaignData.Denumire" :is-editing="isEditing"
                            :show-label="false" />
                    </div>

                    <div class="field-group">
                        <label class="field-label">Industrie</label>
                        <DxSelectBox v-model="internalCampaignData.IndustrieID" :data-source="dsIndustrii"
                            display-expr="Nume" value-expr="ID" :search-enabled="true" :read-only="!isEditing"
                            placeholder="Selectează industrie" />
                    </div>

                    <div class="field-group">
                        <label class="field-label">Buget Estimat</label>
                        <DxNumberBox v-model="internalCampaignData.Buget" :read-only="!isEditing" format="#,##0.00 RON"
                            :showSpinButtons="false" width="100%" />
                    </div>

                    <div class="field-group">
                        <label class="field-label">Dată Start</label>
                        <DxDateBox v-model="internalCampaignData.DataStart" type="date" display-format="dd.MM.yyyy"
                            :read-only="!isEditing" />
                    </div>

                    <div class="field-group">
                        <label class="field-label">Dată Sfârșit</label>
                        <DxDateBox v-model="internalCampaignData.DataEnd" type="date" display-format="dd.MM.yyyy"
                            :read-only="!isEditing" />
                    </div>

                    <div class="field-group">
                        <label class="field-label">Campanie Activă</label>
                        <DxCheckBox v-model="internalCampaignData.CurentActiv" :read-only="!isEditing" text="Activ" />
                    </div>

                    <div class="field-group full-width">
                        <label class="field-label">Explicație / Note</label>
                        <DxTextArea v-model="internalCampaignData.Explicatie" :read-only="!isEditing" :height="120"
                            placeholder="Adaugă detalii suplimentare..." />
                    </div>
                </div>
            </div>


        </DxValidationGroup>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ToolbarForm from '@/components/utils/toolbar-form.vue';
import FormTextbox from '@/components/utils/form-textbox.vue';
import { DxValidationGroup } from 'devextreme-vue/validation-group';
import { DxNumberBox } from 'devextreme-vue/number-box';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxDateBox } from 'devextreme-vue/date-box';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxTextArea } from 'devextreme-vue/text-area';
import ODataStore from 'devextreme/data/odata/store';
import DataSource from 'devextreme/data/data_source';
import AppSetting from '@/settings/appSettings';
import CrmCampaniiMarketing from '@/services/Comercial/CrmCampaniiMarketing';
import notify from 'devextreme/ui/notify';

export default defineComponent({
    name: 'CampaniiForm',
    components: {
        DxValidationGroup,
        ToolbarForm,
        FormTextbox,
        DxNumberBox,
        DxSelectBox,
        DxDateBox,
        DxCheckBox,
        DxTextArea
    },
    props: {
        campaignData: {
            type: Object,
            required: true
        },
        isLocked: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isEditing: false,
            internalCampaignData: this.campaignData ? JSON.parse(JSON.stringify(this.campaignData)) : {},
            expandedSections: {
                general: true
            },
            dsIndustrii: new DataSource({
                store: new ODataStore({
                    url: AppSetting.API_Url_ComercialOdata + 'CRM_Industrii',
                    key: 'ID',
                    keyType: 'Guid',
                    version: 4,
                    beforeSend: (e) => {
                        e.headers = {
                            'Content-Type': 'application/json',
                            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
                        };
                    },
                }),
                select: ['ID', 'Nume']
            })
        };
    },
    methods: {
        toggleSection(section: string) {
            (this.expandedSections as any)[section] = !(this.expandedSections as any)[section];
        },
        startEdit() {
            if (this.isLocked) {
                notify('Această campanie este blocată și nu mai poate fi editată.', 'warning', 2000);
                return;
            }
            this.isEditing = true;
        },
        cancelEdit() {
            this.internalCampaignData = JSON.parse(JSON.stringify(this.campaignData));
            this.isEditing = false;
        },
        handleSaveClick({ validationGroup }: any) {
            const result = validationGroup.validate();
            if (!result.isValid) {
                console.warn('Formular invalid:', result);
                return;
            }

            const payload = { ...this.internalCampaignData };
            const id = payload.ID;

            if (!id) {
                notify('Lipsește ID-ul campaniei!', 'error', 2000);
                return;
            }

            // Remove expanded/navigation objects
            delete payload.ID;
            delete payload.Industrie;
            delete payload.__metadata;

            CrmCampaniiMarketing.update(id, payload).then(() => {
                notify('Modificările au fost salvate cu succes!', 'success', 2000);
                this.$emit('saved', this.internalCampaignData);
                this.isEditing = false;
            }).catch(err => {
                console.error("Error updating campaign:", err);
                notify('Eroare la salvarea modificărilor!', 'error', 2000);
            });
        }
    },
    watch: {
        campaignData: {
            handler(newVal) {
                if (!this.isEditing) {
                    this.internalCampaignData = newVal ? JSON.parse(JSON.stringify(newVal)) : {};
                }
            },
            deep: true
        }
    }
});
</script>

<style scoped lang="scss">
.salesforce-campaign-form {
    position: relative;
    background: #f3f4f6;
    min-height: 100%;
}

.form-toolbar {
    display: flex;
    justify-content: flex-end;
    padding: 1rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
}

.form-cards-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 0 1.5rem 1.5rem 1.5rem;
}

/* Info Card Styling */
.info-card {
    background: white;
}

.card-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;

    transition: all 0.3s ease;
}

.field-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    &.full-width {
        grid-column: 1 / -1;
    }
}

.field-label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    margin-bottom: 2px;
}

/* DevExtreme Input Overrides */
::v-deep(.dx-texteditor) {
    border-radius: 4px;
    border-color: #d1d5db;
    background: white;

    &.dx-state-readonly {
        background: #f9fafb;
        border-color: #e5e7eb;
    }

    &.dx-state-focused {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &.dx-state-hover:not(.dx-state-readonly) {
        border-color: #9ca3af;
    }
}

::v-deep(.dx-texteditor-input) {
    padding: 8px 12px;
    font-size: 14px;
    color: #111827;
    min-height: 38px;
}

::v-deep(.dx-dropdowneditor-button) {
    border-left: 1px solid #e5e7eb;
}

/* Responsive */
@media (max-width: 768px) {
    .card-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
        padding: 1rem;
    }

    .field-group.full-width {
        grid-column: 1;
    }
}
</style>
